/* eslint-disable jsx-a11y/alt-text */
import {motion} from 'framer-motion'
import {useEffect, useMemo, useRef} from 'react'
import {useAuth} from '../../authentication/AuthProvider'
import {usePatientContext} from './StyleDetailInformationWrapper'

import {ReactComponent as BiBody} from '../../../assets/icons/BiBody.svg'
import {ReactComponent as IoChatBubbles} from '../../../assets/icons/IoChatBubbles.svg'
import {ReactComponent as SettingIcon} from '../../../assets/icons/SettingIcon.svg'
import {ReactComponent as TbCameraPlus} from '../../../assets/icons/TbCameraPlus.svg'
import {ReactComponent as AssignHCP} from '../../../assets/menu/AssignHCP.svg'

import {ReactComponent as DobIcon} from '../../../assets/icons/DobIcon.svg'
import {ReactComponent as HospitalIcon} from '../../../assets/icons/HospitalIcon.svg'
import {ReactComponent as HospitalNumberIcon} from '../../../assets/icons/HospitalNumberIcon.svg'
import {ReactComponent as NHSNumberIcon} from '../../../assets/icons/NHSNumberIcon.svg'

import {ReactComponent as FaDoctor} from '../../../assets/icons/FaDoctor.svg'
import {ReactComponent as FaThermometerHalf} from '../../../assets/icons/FaThermometerHalf.svg'
import {ReactComponent as MdOutlineAssignmentTurnedIn} from '../../../assets/icons/MdOutlineAssignmentTurnedIn.svg'

import {Button} from 'primereact/button'
import {Tag} from 'primereact/tag'
import defaultAvatar from '../../../assets/images/avatar.jpg'
import {capitalize} from '../../../utils/CommonUtils'

import {useQueryClient} from '@tanstack/react-query'
import moment from 'moment'
import {Avatar} from 'primereact/avatar'
import {SplitButton} from 'primereact/splitbutton'
import {Tooltip} from 'primereact/tooltip'
import {SystemRole} from '../../../Constants'
import {ReactComponent as MdSick} from '../../../assets/icons/MdSick.svg'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {log} from '../../../log/Logs'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../../../query-manager/QueryKeysManager'
import {getClientId} from '../../authentication/AuthUtils'
import BiometricsForm from '../../biometrics/BiometricsForm'
import SendMessageForm from '../../message/SendMessageForm'
import {isArrayAndNotEmpty} from '../../observations/Utils'
import {permissionOptionKeys} from '../../role/RoleConstant'
import SummaryTable from '../../summary/SummaryTable'
import {convertKeyToLabel} from '../../table-utils/TableUtils'
import StyledAssignmentHealthCarePractitioner from '../StyledAssignmentHealthCarePractitioner'
import StyledAssignmentPathway from '../StyledAssignmentPathway'
import StyledAssignmentStaticList from '../StyledAssignmentStaticList'
import StyledAssignmentSupportTeam from '../StyledAssignmentSupportTeam'
import '../styles.css'
import {
    getSupportTeamEventKey,
    supportTeamEvent,
    supportTeamEventTypes,
} from '../support-team/broadcast/SupportTeamEvent'
import StyledChangeAvatarForm from './StyledChangeAvatarForm'
import StyledPatientPortalSettingsForm from './StyledPatientPortalSettingsForm'
const iconStyle = {
    width: '18px',
    height: '18px',
    color: '#6c757d',
    marginRight: '6px',
}

const statusMapping = {
    normal: {severity: 'success', value: 'NORMAL'},
    mild: {severity: 'info', value: 'MILD'},
    moderate: {severity: 'warning', value: 'MODERATE'},
    severe: {severity: 'danger', value: 'SEVERE'},
}

const StyledBasicInformation = ({isFullScreen = false}) => {
    //#region Context
    const {currentViewedUser, handleCheckPermissionByKey} = usePatientContext()
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    //#endregion
    //#region Translate
    const {
        translateKeys,
        translateCommons,
        translateDatatable,
        translateOptions,
    } = TranslateUtils()
    //#endregion
    const queryClient = useQueryClient()
    const changeAvatarRef = useRef(null)
    const biometricsRef = useRef(null)
    const hcpAssignmentRef = useRef(null)
    const staticListAssignmentRef = useRef(null)
    const pathwayAssignmentRef = useRef(null)
    const supportTeamRef = useRef(null)
    const summaryRef = useRef(null)
    const patientPortalSettingRef = useRef(null)
    const sendMessageRef = useRef(null)
    //#region Information
    const basicInformationList = useMemo(
        () => [
            {
                label: 'Date of birth',
                value: currentViewedUser?.dob
                    ? `${currentViewedUser?.dob} ${
                          currentViewedUser?.age
                              ? `(${currentViewedUser?.age} y.o)`
                              : '-'
                      }`
                    : '-',
                icon: <DobIcon />,
            },
            {
                label: 'Hospital Name',
                value: currentViewedUser?.hospitalName,
                icon: <HospitalIcon />,
            },
            {
                label: 'Hospital No',
                value: currentViewedUser?.hospitalNumber,
                icon: <HospitalNumberIcon />,
            },
            {
                label: 'NHS Number',
                value: currentViewedUser?.nhsNumber,
                icon: <HospitalIcon />,
            },
            {
                label: 'Emergency Contact',
                // value: currentViewedUser?.nhsNumber,
                values: [
                    {
                        label: 'Name',
                        value:
                            currentViewedUser?.emergencyContact?.fullName ||
                            '-',
                    },
                    {
                        label: 'Phone',
                        value:
                            currentViewedUser?.emergencyContact?.phone || '-',
                    },
                ],
                icon: <HospitalIcon />,
            },
            {
                label: 'Arrival Date',
                value: currentViewedUser?.createdAt
                    ? moment(currentViewedUser?.createdAt).format(
                          'HH:mm DD-MM-YYYY ',
                      )
                    : '-',
                icon: <NHSNumberIcon />,
            },
            {
                label: 'Discharge Date',
                value:
                    currentViewedUser?.deletedAt &&
                    currentViewedUser?.accountStatus === 'discharged'
                        ? moment(currentViewedUser?.deletedAt).format(
                              'HH:mm DD-MM-YYYY ',
                          )
                        : '-',
                icon: 'pi pi-map-marker',
            },
        ],
        [currentViewedUser],
    )
    //#endregion

    //#region Menu - More Actions
    const items = [
        {
            label: (
                <span className="font-bold text-darkcyan">
                    Profile Settings
                </span>
            ),
            disabled: true,
        },
        {
            label: 'Change Avatar',
            icon: <TbCameraPlus style={iconStyle} />,
            visible: handleCheckPermissionByKey(
                permissionOptionKeys.allowToEdit,
            ),
            command: () => {
                changeAvatarRef.current.showForm()
            },
        },
        {
            label: 'Edit BMI',
            icon: <BiBody style={iconStyle} />,
            visible: handleCheckPermissionByKey(
                permissionOptionKeys.allowToAddBiometrics,
            ),
            command: () => {
                biometricsRef.current.showForm(
                    currentViewedUser ? {...currentViewedUser?.biometrics} : {},
                )
            },
        },
        {
            separator: true,
        },
        {
            label: (
                <span className="font-bold text-darkcyan">
                    Patient Portal Settings
                </span>
            ),
            disabled: true,
        },
        {
            label: 'Change Setting',
            icon: <SettingIcon style={iconStyle} />,

            command: () => patientPortalSettingRef.current.showForm(),
        },
        {
            separator: true,
        },
        {
            label: <span className="font-bold text-darkcyan">Assignment</span>,
            disabled: true,
        },
        {
            label: 'Edit Support Team',
            icon: <AssignHCP style={iconStyle} />,

            command: () => supportTeamRef.current.showForm(),
        },
        {
            label: 'Edit Pathway',
            icon: <FaThermometerHalf style={iconStyle} />,
            visible: handleCheckPermissionByKey(
                permissionOptionKeys.allowToAssignPathway,
            ),
            command: () => pathwayAssignmentRef.current.showForm(),
        },
        {
            label: 'Edit HC.Practitioner',
            icon: <FaDoctor style={iconStyle} />,
            visible: handleCheckPermissionByKey(
                permissionOptionKeys.allowToAssignHcp,
            ),
            command: () => hcpAssignmentRef.current.showForm(),
        },
        {
            label: 'Edit Static List',
            icon: <MdOutlineAssignmentTurnedIn style={iconStyle} />,
            visible: handleCheckPermissionByKey(
                permissionOptionKeys.allowToAssignStaticList,
            ),
            command: () => staticListAssignmentRef.current.showForm(),
        },
        {
            separator: true,
        },
        {
            label: <span className="font-bold text-darkcyan">Contact</span>,
            disabled: true,
        },
        {
            label: 'Send Message',
            icon: <IoChatBubbles style={iconStyle} />,
            command: () => sendMessageRef.current.showForm(currentViewedUser),
        },
    ]
    //#endregion

    //#region Event Listener on State Changes
    useEffect(() => {
        // USER SUPPORT TEAM CHANGE
        supportTeamEvent.on(
            getSupportTeamEventKey(
                supportTeamEventTypes.USER_SUPPORT_TEAM_CHANGE,
            ),
            data => {
                if (
                    !data ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (
                    userRole === SystemRole.USER_WITH_ROLE &&
                    (!data?.hospitalId ||
                        data?.hospitalId !== currentHospital?.id)
                ) {
                    return
                }

                if (currentViewedUser?.id !== data?.userId) {
                    return
                }

                const {supportTeamKeys} = QueryKeyManager(
                    QueryKeyTypes.supportTeam,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: supportTeamKeys.pattern(),
                })
            },
        )
    }, [])

    return (
        <div className="w-full">
            {log('Basic Information Rendered')}
            {/* Patient Information */}
            <div className="relative h-full">
                <div className={`col-12`}>
                    <div
                        className={`flex flex-column justify-content-between align-items-center mb-3`}>
                        {/* Avatar */}
                        <div className="pt-4">
                            <Avatar
                                className="basic-info-avatar"
                                image={
                                    currentViewedUser?.avatar
                                        ? currentViewedUser?.avatar
                                        : defaultAvatar
                                }
                                shape="circle"
                                alt="Avatar"
                                // style={{width: 120, height: 120}}
                            />
                        </div>

                        <div className="flex flex-column align-items-center justify-content-between ">
                            {' '}
                            <h3 className="basic-info-name font-bold mb-1 text-center">
                                {currentViewedUser?.title &&
                                currentViewedUser.title !== 'unknown'
                                    ? `${capitalize(currentViewedUser?.title)} `
                                    : ''}
                                {currentViewedUser?.fullName
                                    ? capitalize(currentViewedUser?.fullName)
                                    : '-'}
                            </h3>
                            <p className="basic-info-gender text-600 font-semibold">
                                {currentViewedUser?.gender
                                    ? convertKeyToLabel(
                                          currentViewedUser?.gender,
                                      )
                                    : ''}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex gap-1 mb-4">
                    {' '}
                    <div className="col-6 m-0 p-0">
                        {' '}
                        {handleCheckPermissionByKey(
                            permissionOptionKeys.allowToViewSummary,
                        ) && (
                            <Button
                                className="text-sm bg-blue-50 hover:bg-blue-100 border-none text-blue-500 w-full h-3rem"
                                iconPos="left"
                                icon="pi pi-eye"
                                label={translateKeys('SUMMARY')}
                                onClick={() => summaryRef.current.showForm()}
                            />
                        )}{' '}
                    </div>
                    <div className="col-6 m-0 p-0">
                        {' '}
                        <SplitButton
                            menuClassName="custom-disability"
                            className="w-full h-3rem"
                            model={items}
                            severity="info"
                            // icon="pi pi-ellipsis-v"
                            label={translateKeys('ACTIONS')}
                        />
                    </div>
                </div>
                <div className="flex">
                    <h5 className="basic-info-tittle font-semibold text-darkcyan bg-teal-50  mb-2 font-bold px-2 py-3  pl-3 w-20rem border-round-right-xl">
                        <i className="pi pi-info-circle" />{' '}
                        {translateKeys('General Information')}
                    </h5>
                </div>

                <div
                    className={`grid align-items-strecth styled-basic-info-wrapper col-12 mb-3`}>
                    {basicInformationList?.map((item, index) => (
                        <div
                            key={`${item?.label}-${index}`}
                            className={`col-12 `}>
                            <div className="h-full grid border-bottom-1 border-100 w-full pb-2">
                                <h6 className="col-6 m-0 p-0 text-darkcyan font-bold ">
                                    {translateKeys(item?.label)}
                                </h6>

                                {item.values &&
                                isArrayAndNotEmpty(item.values) ? (
                                    <div className="flex flex-column col-6 m-0 p-0 gap-2">
                                        {item.values.map((valueItem, idx) => (
                                            <div className="" key={idx}>
                                                {' '}
                                                <h6 className="text-600 m-0 mb-1 p-0 font-semibold ">
                                                    {translateKeys(
                                                        valueItem.label,
                                                    )}{' '}
                                                    :
                                                </h6>
                                                <h6 className="m-0 p-0 font-semibold ">
                                                    {valueItem.value}
                                                </h6>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <h6 className="col-6  m-0 p-0 font-semibold ">
                                        {item?.value ? item.value : '-'}
                                    </h6>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex">
                    <h5 className="basic-info-tittle  font-semibold text-darkcyan bg-teal-50  mb-2 font-bold px-2 py-3  pl-3 w-20rem border-round-right-xl flex align-items-center gap-2">
                        <MdSick style={{width: '18px', height: '18px'}} />{' '}
                        {translateKeys('Pathway')}
                    </h5>
                </div>
                <div className="col-12 flex justify-content-center">
                    {!!isArrayAndNotEmpty(currentViewedUser?.pathways) ? (
                        <div className="flex flex-grow-1 flex-wrap gap-2 ">
                            {currentViewedUser?.pathways.map((item, idx) => (
                                <div
                                    key={idx}
                                    id={`pathway-tooltip-${item.id}`}
                                    className="flex align-items-center justify-content-center p-2 border-round-lg hover:bg-gray-50 ">
                                    <img
                                        src={item.icon}
                                        height={40}
                                        width={40}
                                    />
                                    <Tooltip
                                        target={`#pathway-tooltip-${item.id}`}
                                        content={item.name}
                                        position="top"
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        ''
                    )}
                </div>

                {/* Summary Status */}
                {!!statusMapping[currentViewedUser?.sicknessStatus]
                    ?.severity && (
                    <motion.div
                        className="absolute top-0 left-0"
                        animate={{
                            opacity: [1, 0.6, 1],
                        }}
                        transition={{
                            repeat: Infinity,
                            duration: 1.5,
                            repeatType: 'loop',
                        }}>
                        <Tag
                            className="px-3"
                            severity={
                                statusMapping[currentViewedUser?.sicknessStatus]
                                    .severity
                            }
                            value={
                                statusMapping[currentViewedUser?.sicknessStatus]
                                    .value
                            }
                        />
                    </motion.div>
                )}
            </div>

            {/* Form Ref */}

            {/* Change Avatar Form */}
            <StyledChangeAvatarForm ref={changeAvatarRef} />

            {/* Biometric Form */}
            <BiometricsForm ref={biometricsRef} />

            {/* Assign Health Care Practitioner Form */}
            <StyledAssignmentHealthCarePractitioner
                userIds={!!currentViewedUser?.id ? [currentViewedUser?.id] : []}
                ref={hcpAssignmentRef}
            />

            {/* Assign Static List Form */}
            <StyledAssignmentStaticList
                userIds={!!currentViewedUser?.id ? [currentViewedUser?.id] : []}
                ref={staticListAssignmentRef}
            />

            {/* Assign Pathway Form */}
            <StyledAssignmentPathway
                userIds={!!currentViewedUser?.id ? [currentViewedUser?.id] : []}
                ref={pathwayAssignmentRef}
            />

            {/* Support Team Form */}
            <StyledAssignmentSupportTeam
                userIds={!!currentViewedUser?.id ? [currentViewedUser?.id] : []}
                ref={supportTeamRef}
            />

            {/* Summary Form */}
            <SummaryTable ref={summaryRef} />

            {/* Patient Portal Setting Form */}
            <StyledPatientPortalSettingsForm ref={patientPortalSettingRef} />

            {/* Message Form */}
            <SendMessageForm ref={sendMessageRef} />
        </div>
    )
}

export default StyledBasicInformation
